import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useMachine } from "@xstate/react";
import {
    CheckBoxSelection,
    DataTableCallbackRefs,
    QueueDelete,
    QueueEdit,
    clearAllSelection,
    QueueContext,
    DataTableGridOptions,
    TIME_RANGE_FROM,
    TIME_RANGE_TO,
} from "@iventis/data-table";
import { css, Theme } from "@emotion/react";
import { Body2, FormLabel, Header5 } from "@iventis/styles/src/components/texts";
import { Content } from "@iventis/translations/content/typed-content";
import { greyTheme, hiddenTextCss, iconButtonWidth, styled } from "@iventis/styles";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { MapObject } from "@iventis/domain-model/model/mapObject";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Feature } from "geojson";
import { CheckedCheckBox, InteractiveElement, UncheckedCheckBox } from "@iventis/styles/src/components";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { DataField } from "@iventis/domain-model/model/dataField";
import { useLocation } from "react-router";
import { canEdit, timeRange, timeRangeTo, timeRangeFrom, useConstant, extractDataFieldValue, parseDataFieldValuesWithRepeatedTimeRange, isAdmin } from "@iventis/utilities";
import { toast } from "@iventis/toasts/src/toast";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { DomainLayer } from "@iventis/map-engine/src/state/map.state.types";
import { mapColumnIdsToName, removeFieldSuffixes } from "@iventis/data-table/helpers/column-generators";
import { filterByUserLayers } from "@iventis/map-engine/src/utilities/layer.helpers";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { useDataFieldServices } from "@iventis/datafield-editor";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { TreeBrowserDomainNodeContext, useTreeBrowser } from "@iventis/tree-browser";
import { IconButton, useTheme } from "@mui/material";
import { Sitemap } from "@iventis/domain-model/model/sitemap";
import { CheckBoxStyled, StyledPopover } from "@iventis/components";
import { MappingEngine } from "@iventis/map-engine";
import { LocalGeoJson, SelectedMapObject } from "@iventis/map-types";
import { getMapObjectNameSystemDatafield, getSystemDataFieldToIdMapping, isQuantityDataFieldValueAffectedByDataField, updateMapObjectsQuantityValue } from "@iventis/datafields";
import { MapSitemapConfig } from "@iventis/domain-model/model/mapSitemapConfig";
import { SystemDataFieldName } from "@iventis/domain-model/model/systemDataFieldName";
import { assign } from "xstate";
import { DataTable } from "@iventis/data-table/components/data-table";
import { DataTableOperationsComponent } from "./data-table-operations";
import { getDataSource, dataTablePageSize } from "../services/data-table-source";
import { RootDispatch, RootState, rootStore } from "../state/root.store";
import { StyledDataTableWrapper, StyledToolBar, StyledColumnsDisplayedButton, StyledChecklistPopover } from "./sidebar-data-table.styles";
import { getMapIdWithLocation } from "../map.helpers";
import { MapObjectRowNodeData } from "./sidebar-data-table.types";
import { deleteObjects, patchMapObjectsDataFieldValues } from "../state/map.slice.thunks";
import { map } from "../state/map.slice";
import { createColumns, onFilterChange, LAYER_ID_FIELD_NAME, subscriptionToMapObjectChanges, onPaginationChanged } from "./sidebar-data-table-helpers";
import { getSidebarDataTableInitialState, sidebarDataTableMachine } from "./sidebar-data-table-reducer";
import { getAllSitemapLevels } from "../sidebar-library/sidebar-nodes";
import { handleSelectedMapObject } from "../map-object-selection-helpers";
import { SidebarBarDataTableRepeatedTimeRangeComponent } from "./sidebar-data-table-repeated-time-range";
import { useMapUndoRedoContext } from "../map-undo-redo-context";

export const FROM_DATA_TABLE = "data-table";

// Grid dimensions
const HEADER_HEIGHT = 44;
const ROW_HEIGHT = 42;

type FilterChangedEvent = Parameters<DataTableGridOptions<MapObjectRowNodeData>["onFilterChanged"]>[0];

export const SidebarDataTableComponent: FunctionComponent<{
    sidebarWidth: number;
    layerPermissions: { [layerId: string]: Authorisation };
    projectDataFields: DataField[];
    siteMaps: Sitemap[];
    updateMapObjectName: (nameUpdate: { id: string; name: string }) => void;
    setShowObjectDeletionDialog?: (showObjectDeletionDialog: boolean) => void;
}> = ({ sidebarWidth, layerPermissions, projectDataFields, siteMaps, updateMapObjectName, setShowObjectDeletionDialog }) => {
    const theme = useTheme<Theme>();
    const location = useLocation();
    const mapId = getMapIdWithLocation(location);
    const translate = useIventisTranslate();
    const dispatch: RootDispatch = useDispatch();
    const { dataFieldListItemsService } = useDataFieldServices();
    const [{ tree, selectedNodeIds: selectedNodes }, treeBrowserContext] = useTreeBrowser(TreeBrowserDomainNodeContext, null, "selectedNodeIds", "tree");

    const [repeatedTimeRangeModal, setRepeatedTimeRangeModal] = useState<{ dataFieldId: string; rowData: MapObjectRowNodeData } | null>(null);

    const [canUserEditSelection, setCanUserEditSelection] = useState(true);

    // Data table callbacks
    const gridRef = useRef<DataTableCallbackRefs<MapObjectRowNodeData>>();

    // Boolean determining if an ellipsis should be used to diplay the table operations
    const usingEllipsisForOperationButtons = sidebarWidth <= 100;

    // Popover state
    const [operationsPopoverOpen, setOperationsPopoverOpen] = useState<boolean>(false);
    const operationsPopoverAnchor = useRef();
    const [columnsDisplayedPopoverOpen, setColumnsDisplayedPopoverOpen] = useState<boolean>(false);
    const columnsDisplayedPopoverAnchor = useRef();

    const siteMapItems = useMemo(() => getAllSitemapLevels(siteMaps), []);
    // All layers in the current map
    const {
        layers,
        unitOfMeasurement,
        mapObjectsInState,
        mapObjectsSelected,
        sitemapConfigs,
        mappingEngine,
    }: {
        layers: DomainLayer[];
        selectedNodes: string[];
        unitOfMeasurement: UnitOfMeasurement;
        mapObjectsInState: LocalGeoJson;
        mapObjectsSelected: SelectedMapObject[];
        sitemapConfigs: MapSitemapConfig[];
        mappingEngine: MappingEngine;
    } = useSelector(
        (s: RootState) => ({
            layers: filterByUserLayers(s.mapReducer.mapModule.layers.value),
            unitOfMeasurement: s.mapReducer.mapModule.unitOfMeasurement.value,
            mapObjectsInState: s.mapReducer.mapModule.geoJSON.value,
            mapObjectsSelected: s.mapReducer.mapModule.mapObjectsSelected.value,
            sitemapConfigs: s.mapReducer.sitemapConfigs,
            mappingEngine: s.mapReducer.mappingEngine,
        }),
        shallowEqual
    );

    const undoRedo = useMapUndoRedoContext();

    const systemDataFieldToIdMapping = getSystemDataFieldToIdMapping(projectDataFields);

    const [tableError, setTableError] = useState<Error | null>(null);

    useEffect(() => {
        if (tableError) {
            throw tableError;
        }
    }, [tableError]);

    const onError = (error: Error) => {
        setTableError(error);
    };

    // Data table state
    const [state, send] = useMachine(sidebarDataTableMachine, {
        context: getSidebarDataTableInitialState({
            dataSource: getDataSource(layerPermissions, systemDataFieldToIdMapping, tree, siteMaps, sitemapConfigs, (e) => {
                onError(e);
            }),
        }),
        actions: {
            setContext: assign((_, event) => {
                const gridContext = { mapId, columnIdsToNameMapping: mapColumnIdsToName(event.data), translate, toast, treeBrowserContext };
                return {
                    gridContext,
                    isTableReady: true,
                };
            }),
        },
        services: {
            getInitialColumns: async () => {
                const columns = await createColumns(
                    layers,
                    initialFilteredLayers ?? layers,
                    setAvailableDatafields,
                    projectDataFields,
                    unitOfMeasurement,
                    dataFieldListItemsService.getDataFieldListItems,
                    handleRepeatedTimeRangeClick,
                    mappingEngine
                );
                return columns;
            },
            filterColumns: async (_, { payload }) => {
                const columns = await onFilterChange(
                    layers,
                    setAvailableDatafields,
                    projectDataFields,
                    unitOfMeasurement,
                    dataFieldListItemsService.getDataFieldListItems,
                    handleRepeatedTimeRangeClick,
                    mappingEngine,
                    payload.filter
                );
                return columns;
            },
        },
    });
    const { grouping, dataTableKey, dataSource, gridContext, columns: availableColumns } = state.context;
    const columnsDisplayed = availableColumns?.reduce((acc, { agGridColumnProps }) => (agGridColumnProps.hide ? acc : [...acc, agGridColumnProps.field]), []) ?? [];

    useEffect(() => {
        const subscription = subscriptionToMapObjectChanges(gridRef, siteMaps);
        return () => subscription.unsubscribe();
    }, []);

    const [availableDatafields, setAvailableDatafields] = useState<DataField[]>();

    const initialFilteredLayers = useConstant(() => (selectedNodes.length > 0 ? layers.filter(({ id }) => selectedNodes.some((selectedId) => selectedId === id)) : undefined));

    const handleRepeatedTimeRangeClick = (dataFieldId: string, rowData: MapObjectRowNodeData) => {
        // Ensures the selected layer has the repeated time range datafield
        const layer = layers.find((l) => l.id === rowData.layerId);
        if (layer.dataFields.some((df) => df.id === dataFieldId)) {
            setRepeatedTimeRangeModal({ dataFieldId, rowData });
        }
    };

    const handleFilterChange = async (event: FilterChangedEvent) => {
        const filters = event.api.getFilterModel();
        send({ type: "FILTER_COLUMNS", payload: { filter: filters?.[LAYER_ID_FIELD_NAME]?.values } });
    };

    const onEdit = async (edit: QueueEdit<MapObjectRowNodeData>) => {
        const editData = edit.node.data;
        const [columnId, newValue] = edit.update;
        const dataField = availableDatafields.find((df) => df.id === removeFieldSuffixes(columnId));
        let editedMapObject: MapObject;
        // Skip over repeated time ranges, there datafield edits are done in a separate modal
        if (dataField && dataField.type !== DataFieldType.RepeatedTimeRanges) {
            const existingValue = editData.dataFieldValues[dataField.id];
            let updatedValue = newValue;
            if (columnId.includes(TIME_RANGE_FROM)) {
                updatedValue = timeRange(newValue, timeRangeTo(existingValue) ?? newValue);
            }
            if (columnId.includes(TIME_RANGE_TO)) {
                updatedValue = timeRange(timeRangeFrom(existingValue) ?? newValue, newValue);
            }
            const level = siteMapItems.find((sml) => sml.name === editData.level)?.index;
            editedMapObject = { ...editData, level, dataFieldValues: { ...editData.dataFieldValues, [dataField.id]: extractDataFieldValue(updatedValue, "id") } };
            const mapObjectNameDataField = getMapObjectNameSystemDatafield(projectDataFields);
            if (columnId === mapObjectNameDataField?.id) {
                updateMapObjectName({ id: editedMapObject.id, name: editedMapObject.dataFieldValues[mapObjectNameDataField.id] });
            }
            const alreadyUpdated = mapObjectsInState[editData.layerId]?.find((o) => o.objectId === editData.id)?.feature?.properties[columnId] === extractDataFieldValue(newValue);

            if (!alreadyUpdated) {
                const layer = layers.find((l) => l.id === editedMapObject.layerId);
                if (isQuantityDataFieldValueAffectedByDataField(layer, dataField?.id)) {
                    const mapObjectsWithUpdatedQuantity = updateMapObjectsQuantityValue([layer], [editedMapObject], projectDataFields);
                    await dispatch(patchMapObjectsDataFieldValues({ mapObjects: mapObjectsWithUpdatedQuantity, from: "data-table" }));
                } else {
                    await dispatch(patchMapObjectsDataFieldValues({ mapObjects: [editedMapObject], from: "data-table" }));
                }
            }
        }
    };

    return (
        <>
            <StyledDataTableWrapper maxWidth={`${sidebarWidth - 10}px`}>
                <StyledToolBar>
                    <LeftSideContainer>
                        <StyledColumnsDisplayedButton
                            data-testid="data-table-column-selector"
                            ref={columnsDisplayedPopoverAnchor}
                            onClick={() => setColumnsDisplayedPopoverOpen(true)}
                        >
                            <Body2>{translate(Content.map.buttons.columns_displayed)}</Body2>
                            <FontAwesomeIcon className="icon" icon={["fas", "sort-down"]} />
                        </StyledColumnsDisplayedButton>
                        <CheckboxContainer>
                            <CheckBoxStyled checked={grouping} onChange={(e) => send({ type: "SET_GROUPING", payload: e.currentTarget.checked })} testId="group-data-toggle" />
                            <FormLabel>{translate(Content.map7.dataTableGrouping.checkbox)}</FormLabel>
                        </CheckboxContainer>
                    </LeftSideContainer>
                    <StyledChecklistPopover
                        open={columnsDisplayedPopoverOpen}
                        anchorEl={columnsDisplayedPopoverAnchor.current}
                        onClose={() => setColumnsDisplayedPopoverOpen(false)}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "left",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "left",
                        }}
                    >
                        <StyledColumnSelectorHeader className="flex-row">
                            <Header5>{translate(Content.map.buttons.columns_displayed)}</Header5>
                            <IconButton className="close-data-table-column-selector" onClick={() => setColumnsDisplayedPopoverOpen(false)}>
                                <FontAwesomeIcon className="icon" fontSize="1rem" icon={["far", "xmark"]} />
                            </IconButton>
                        </StyledColumnSelectorHeader>
                        {availableColumns?.map((column) => (
                            <CheckBoxSelection
                                key={column.agGridColumnProps.field}
                                dataTestId={`data-table-column-selector-${column.agGridColumnProps.headerName}`}
                                value={column.agGridColumnProps.field}
                                checked={!column.agGridColumnProps.hide}
                                displayName={column.agGridColumnProps.headerName}
                                icon={<UncheckedCheckBox size={15} borderStyle={`1.5px solid ${theme.primaryColors.subdued70}`} backgroundColour={theme.typographyColors.blank} />}
                                checkedIcon={
                                    <CheckedCheckBox
                                        size={15}
                                        borderStyle={`1.5px solid ${theme.primaryColors.subdued70}`}
                                        backgroundColour={theme.primaryColors.subdued70}
                                        tickColour={theme.typographyColors.blank}
                                        tickWeight="fas"
                                    />
                                }
                                onChange={(e) => {
                                    const newColumns = availableColumns.map((c) =>
                                        c.agGridColumnProps.field === column.agGridColumnProps.field
                                            ? { ...c, agGridColumnProps: { ...c.agGridColumnProps, hide: !e.currentTarget.checked } }
                                            : c
                                    );
                                    send({ type: "SET_COLUMNS", payload: { columns: newColumns } });
                                }}
                            />
                        ))}
                    </StyledChecklistPopover>
                    {usingEllipsisForOperationButtons ? (
                        <>
                            <InteractiveElement onClick={() => setOperationsPopoverOpen(true)} ref={operationsPopoverAnchor} style={{ padding: "0 20px" }}>
                                <FontAwesomeIcon icon={["fas", "ellipsis-vertical"]} />
                            </InteractiveElement>
                            <StyledPopover
                                space={iconButtonWidth}
                                open={operationsPopoverOpen}
                                onClose={() => setOperationsPopoverOpen(false)}
                                anchorEl={operationsPopoverAnchor.current}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "center",
                                }}
                                transformOrigin={{
                                    vertical: "top",
                                    horizontal: "left",
                                }}
                            >
                                <DataTableOperationsComponent
                                    gridRef={gridRef}
                                    layerIds={layers.map((l) => l.id)}
                                    mapId={mapId}
                                    mapObjectsSelected={mapObjectsSelected}
                                    canUserEdit={canUserEditSelection && selectedNodes.every((id) => isAdmin(layerPermissions[id]))}
                                    dropdown
                                    unitOfMeasurement={unitOfMeasurement}
                                    columnIdsToNameMapping={mapColumnIdsToName(availableColumns)}
                                    columnsDisplayed={columnsDisplayed}
                                />
                            </StyledPopover>
                        </>
                    ) : (
                        <DataTableOperationsComponent
                            gridRef={gridRef}
                            layerIds={layers.map((l) => l.id)}
                            mapId={mapId}
                            mapObjectsSelected={mapObjectsSelected}
                            canUserEdit={canUserEditSelection && selectedNodes.every((id) => isAdmin(layerPermissions[id]))}
                            unitOfMeasurement={unitOfMeasurement}
                            columnIdsToNameMapping={mapColumnIdsToName(availableColumns)}
                            columnsDisplayed={columnsDisplayed}
                        />
                    )}
                </StyledToolBar>
                <div style={{ height: "100%" }}>
                    {gridContext && (
                        <DataTable<MapObjectRowNodeData>
                            key={dataTableKey}
                            ref={gridRef}
                            gridOptions={{
                                serverSideDatasource: dataSource,
                                rowModelType: "serverSide",
                                paginationPageSize: dataTablePageSize,
                                cacheBlockSize: dataTablePageSize,
                                maxBlocksInCache: 10,
                                onSortChanged: (params) => {
                                    clearAllSelection(params.api);
                                },
                                headerHeight: HEADER_HEIGHT,
                                rowHeight: ROW_HEIGHT,
                                context: gridContext,
                                rowSelection: "multiple",
                                getRowId: (row) => row.data.id,
                                onFilterChanged: handleFilterChange,
                                onPaginationChanged,
                                onFirstDataRendered: (params) => {
                                    if (initialFilteredLayers != null) {
                                        // Once our filter is loaded, we can tell the api that the filter has been applied and to refresh
                                        params.api.getFilterInstance("layerId", () => {
                                            params.api.onFilterChanged();
                                        });
                                    }
                                },
                                getRowStyle: (params) => {
                                    // If we're grouping, obverride the row colour to be based on the level
                                    if (grouping) {
                                        return {
                                            background: params.node.level === 0 ? theme.secondaryColors.light20 : theme.secondaryColors.blank,
                                        };
                                    }
                                    return {};
                                },
                                // Part of the bottom of the table and the horizontal scrollbar will be obscured without this option
                                domLayout: "normal",
                            }}
                            columns={availableColumns}
                            defaultSort={[
                                { colId: "layerId", sort: "asc", sortIndex: 0 },
                                { colId: systemDataFieldToIdMapping[SystemDataFieldName.MapObjectName], sort: "asc", sortIndex: 1 },
                            ]}
                            onRowSelected={(nodes) => {
                                const mapState = rootStore.getState().mapReducer.mapModule;
                                if (nodes.length === 1 && mappingEngine === MappingEngine.Mapbox) {
                                    const { currentLevel } = rootStore.getState().mapReducer.mapModule;
                                    const { sitemaps } = rootStore.getState().mapReducer;
                                    const levels = getAllSitemapLevels(sitemaps);
                                    const currentMapObjectLevel = levels.length > 1 ? nodes[0]?.data?.geoJsonFeature?.properties.level : 0;

                                    const upToDateLayers = filterByUserLayers(mapState.layers.value);
                                    const layer = upToDateLayers.find((l) => l.id === nodes[0].data.layerId);

                                    // Construct the map object from the nodes data
                                    const mapObject: Feature = {
                                        type: "Feature",
                                        ...nodes[0].data.geoJsonFeature,
                                        properties: parseDataFieldValuesWithRepeatedTimeRange({ ...nodes[0].data.dataFieldValues }, "number"),
                                    };
                                    // pan to map object and show toast if layer is hidden or on a different level
                                    handleSelectedMapObject(
                                        { ...layer, authorisation: layerPermissions[layer.id] },
                                        mapObject,
                                        currentMapObjectLevel,
                                        currentLevel,
                                        dispatch,
                                        map,
                                        translate,
                                        mapState.datesFilter.value
                                    );
                                }
                                const currentSelection = mapState.mapObjectsSelected.value?.map(({ objectId }) => objectId);
                                const selection = nodes ?? [];
                                const selectionAsIds = selection.map(({ data }) => data.id);
                                // If selection is the same, then don't do anything (it's likely the selection came from outside)
                                if (selectionAsIds.every((id) => currentSelection.includes(id)) && currentSelection.every((id) => selectionAsIds.includes(id))) {
                                    return;
                                }
                                // Has user got Read or Admin on all the selected layers
                                setCanUserEditSelection(
                                    mappingEngine === MappingEngine.Mapbox &&
                                        (selection.length === 0 ? true : selection.every(({ data }) => canEdit(layerPermissions[data.layerId])))
                                );

                                // What we send to the tree browser depends entirely on how we arrived at the given selection
                                // If multi selecting and there exists a node that isn't in the current selection, we know we have ctrl added on
                                const isCtrlAdd = selectionAsIds.length > 1 && selectionAsIds.some((id) => !currentSelection.includes(id));
                                // If multi selecting and we're not ctrl adding, then we must be ctrl removing
                                const isCtrlRemove = selectionAsIds.length > 1 && !isCtrlAdd;
                                if (isCtrlRemove) {
                                    // When removing, just find the nodes in the table that are in the current selection but not in the new selection
                                    const newSelection = currentSelection.filter((id) => {
                                        const api = gridRef.current?.getApi?.();
                                        const row = api.getRowNode(id);
                                        return row == null || selectionAsIds.includes(id);
                                    });
                                    treeBrowserContext.toggleSelectNodes(newSelection, { from: FROM_DATA_TABLE, ignoreExistingSelection: true });
                                    return;
                                }
                                if (isCtrlAdd) {
                                    // When adding, just send all the current selection and new selection
                                    treeBrowserContext.toggleSelectNodes(
                                        // Ensure they are distinct
                                        [...currentSelection, ...selectionAsIds].reduce<string[]>((acc, id) => (acc.includes(id) ? acc : [...acc, id]), []),
                                        { from: FROM_DATA_TABLE, ignoreExistingSelection: true }
                                    );
                                    return;
                                }
                                // Else, just select the one
                                treeBrowserContext.toggleSelectNodes(selectionAsIds, { from: FROM_DATA_TABLE, ignoreExistingSelection: true });
                            }}
                            editRowAsync={async (context) => {
                                const queuedEdits = context.queue as QueueContext<MapObjectRowNodeData>[];
                                // eslint-disable-next-line no-restricted-syntax
                                for (const edit of queuedEdits) {
                                    switch (edit.operation) {
                                        case "edit":
                                            await onEdit(edit);
                                            break;
                                        default:
                                            throw new Error(`Edit operation ${edit.operation} not handled`);
                                    }
                                }
                                return Promise.resolve(undefined);
                            }}
                            onDeleteRequested={() => {
                                gridRef.current.deleteRequest(false);
                            }}
                            deleteRowAsync={async (context) => {
                                const queue = context.queue[0] as QueueDelete<MapObjectRowNodeData>;
                                const { row } = queue;
                                const { id, layerId } = row;
                                await dispatch(deleteObjects({ mapObjects: [{ objectId: id, layerId }], modifyStore: true }));
                                undoRedo.registerDelete([{ objectId: id, layerId }]);
                                return row;
                            }}
                            setShowObjectDeletionDialog={setShowObjectDeletionDialog}
                            grouping={grouping ? "serverSideGrouping" : "none"}
                            theme={greyTheme(theme, {
                                additionalCss: css`
                                    .index-cell {
                                        border: none;
                                        background-color: transparent;
                                    }
                                    /* The below is necessary to prevent flickering of an attempted focus on a group cell */
                                    .ag-cell-focus {
                                        transition: background-color, border;
                                        transition-delay: 1ms;
                                    }
                                    .ag-row-selected {
                                        .index-cell {
                                            background-color: transparent;
                                        }
                                    }
                                `,
                            })}
                            shouldRowBeIndexed={(row) => !(row.level > 0)}
                            onRowGroupChanged={(columns) => {
                                const updatedColumns = availableColumns.map((c) => {
                                    const affectedColumn = columns.find((col) => col.getColId() === c.agGridColumnProps.field);
                                    if (affectedColumn == null) {
                                        return c;
                                    }
                                    return { ...c, agGridColumnProps: { ...c.agGridColumnProps, hide: affectedColumn.isRowGroupActive() } };
                                });
                                send({
                                    type: "SET_COLUMNS",
                                    payload: {
                                        columns: updatedColumns,
                                    },
                                });
                            }}
                            onColumnVisibilityChanged={(colId, visible) => {
                                const newColumns = availableColumns.map((c) =>
                                    c.agGridColumnProps.field === colId ? { ...c, agGridColumnProps: { ...c.agGridColumnProps, hide: !visible } } : c
                                );
                                send({ type: "SET_COLUMNS", payload: { columns: newColumns } });
                            }}
                            autoSizeColumnsOnGridSizeChanged
                        />
                    )}
                </div>
            </StyledDataTableWrapper>
            {repeatedTimeRangeModal != null && (
                <SidebarBarDataTableRepeatedTimeRangeComponent
                    dataFieldId={repeatedTimeRangeModal.dataFieldId}
                    rowData={repeatedTimeRangeModal.rowData}
                    onClose={() => setRepeatedTimeRangeModal(null)}
                />
            )}
        </>
    );
};

const CheckboxContainer = styled.span`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const LeftSideContainer = styled.span`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 24px;
`;

const StyledColumnSelectorHeader = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    align-items: center;
    .close-data-table-column-selector {
        /* So keep it consistent with the checkboxes inside the column selector */
        width: 34.8px;
        height: 34.8px;
        ${hiddenTextCss}
        color: ${({ theme }: { theme: Theme }) => theme.typographyColors.lessSubdued}
    }
`;
